//Import
import React, { useMemo } from "react";
import styled from "styled-components";
import styles from "./ModalCronograma_styles";
import { buildCronograma } from "../../helpers/buildCronograma";
import { formatNumber } from "../../helpers/formatNumber";

const DIAS_SEMANA = ["L", "M", "M", "J", "V", "S", "D"];

// Formatea un monto en pesos y, cuando supera las 3 cifras (>= 1.000), lo
// muestra en notación compacta (3.500 -> "$3.5 k", 1.200.000 -> "$1.2 M")
// para que el número no rompa el diseño de las celdas, que son muy angostas.
const formatMonto = (valor) => {
  const numero = Number(valor) || 0;
  const absoluto = Math.abs(numero);

  if (absoluto < 1000) {
    return formatNumber(numero);
  }

  const signo = numero < 0 ? "-" : "";
  let divisor = absoluto >= 1000000 ? 1000000 : 1000;
  let sufijo = divisor === 1000000 ? "M" : "k";

  // Si al redondear a 1 decimal el resultado llega a 1000 (p.ej. 999.999 ->
  // "1000 k"), se sube a la siguiente unidad (1 M) en vez de mostrar "1000 k".
  if (divisor === 1000 && absoluto / divisor >= 999.95) {
    divisor = 1000000;
    sufijo = "M";
  }

  let compacto = (absoluto / divisor).toFixed(1);
  if (compacto.endsWith(".0")) {
    compacto = compacto.slice(0, -2);
  }

  return `${signo}$${compacto} ${sufijo}`;
};

// Componente base
const ModalCronograma_base = ({
  dataModalDetalle,
  dataModalDetalleAbonos,
  dataModalDetalleCuotas,
  ...props
}) => {
  const fechaUltimoPagoProgramado =
    dataModalDetalle && dataModalDetalle !== ""
      ? dataModalDetalle.fecha_ultimo_pago_programado
      : null;

  const { resumen, semanas, estadoVacio } = useMemo(() => {
    const cuotas =
      dataModalDetalleCuotas && dataModalDetalleCuotas.cuotas
        ? dataModalDetalleCuotas.cuotas
        : [];
    const abonos =
      dataModalDetalleAbonos && dataModalDetalleAbonos.abonos
        ? dataModalDetalleAbonos.abonos
        : [];
    return buildCronograma({ cuotas, abonos, fechaUltimoPagoProgramado });
  }, [dataModalDetalleCuotas, dataModalDetalleAbonos, fechaUltimoPagoProgramado]);

  if (!resumen) {
    return (
      <div {...props}>
        <p className="cronograma-vacio">
          {estadoVacio === "fechas_invalidas"
            ? "No se pudo cargar el cronograma: las fechas de las cuotas llegaron en un formato inesperado."
            : "Aún no hay cuotas registradas para este crédito."}
        </p>
      </div>
    );
  }

  return (
    <div {...props}>
      <div className="resumen-grid">
        <div className="resumen-item">
          <span className="label">Inicio</span>
          <span className="valor">{resumen.inicioLabel}</span>
        </div>
        <div className="resumen-item">
          <span className="label">Fin</span>
          <span className="valor">{resumen.finLabel}</span>
        </div>
        <div className="resumen-item">
          <span className="label">Cuotas</span>
          <span className="valor">{resumen.totalCuotas}</span>
        </div>
        <div className="resumen-item">
          <span className="label">Pagadas</span>
          <span className="valor">{resumen.pagadas}</span>
        </div>
        <div className="resumen-item">
          <span className="label">Parcial</span>
          <span className="valor">{resumen.parcial}</span>
        </div>
        <div className="resumen-item">
          <span className="label">Sin abono</span>
          <span className="valor">{resumen.sinAbono}</span>
        </div>
        <div className="resumen-item resumen-item-full">
          <span className="label">Pendiente</span>
          <span className="valor">{resumen.pendiente}</span>
        </div>
      </div>

      <div className="leyenda">
        <span className="leyenda-item">
          <span className="punto estado-pagada" />
          Pagada
        </span>
        <span className="leyenda-item">
          <span className="punto estado-parcial" />
          Parcial
        </span>
        <span className="leyenda-item">
          <span className="punto estado-sin_abono" />
          Sin abono
        </span>
        <span className="leyenda-item">
          <span className="punto estado-pendiente" />
          Pendiente
        </span>
      </div>

      <div className="dias-semana-header">
        {DIAS_SEMANA.map((letra, idx) => (
          <span key={`dow-${idx}`}>{letra}</span>
        ))}
      </div>

      {semanas.map((semana) => (
        <React.Fragment key={semana.key}>
          {semana.headerLabel && (
            <div className="mes-header">{semana.headerLabel}</div>
          )}
          <div className="semana-grid">
            {semana.dias.map((dia) =>
              dia.tieneCuota ? (
                <div
                  key={dia.key}
                  className={`dia-celda con-cuota estado-${dia.estado} ${
                    dia.esInicio ? "marca-inicio" : ""
                  } ${dia.esFin ? "marca-fin" : ""} ${
                    dia.esHoy ? "marca-hoy" : ""
                  }`}
                >
                  <span className="numero-cuota">#{dia.numero}</span>
                  <span className={`fecha-circulo estado-${dia.estado}`}>
                    {dia.dayOfMonth}
                  </span>
                  <span className="separador" />
                  {dia.estado === "sin_abono" ? (
                    <span className="info-pago">
                      <span className="monto">$0</span>
                      <span className="detalle">sin abono</span>
                    </span>
                  ) : dia.estado === "pendiente" ? (
                    <span className="info-pago">
                      <span className="monto monto-solo">
                        {formatMonto(dia.valorCuota)}
                      </span>
                    </span>
                  ) : (
                    <span className="info-pago">
                      <span className="monto">
                        +{formatMonto(dia.montoAbonado)}
                      </span>
                      <span className="detalle">
                        {formatMonto(dia.saldoRestante)}
                      </span>
                    </span>
                  )}
                  {(dia.esInicio || dia.esFin || dia.esHoy) && (
                    <span className="marca-label">
                      {[
                        dia.esInicio && "inicio",
                        dia.esFin && "fin",
                        dia.esHoy && "hoy",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  )}
                </div>
              ) : (
                <div
                  key={dia.key}
                  className={`dia-celda sin-cuota ${
                    dia.esDomingo ? "domingo" : "neutra"
                  } ${dia.esHoy ? "marca-hoy" : ""}`}
                >
                  <span className="fecha-simple">{dia.dayOfMonth}</span>
                  {dia.esHoy && <span className="marca-label">hoy</span>}
                </div>
              )
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const ModalCronograma = styled(ModalCronograma_base)`
  ${styles}
`;

export default ModalCronograma;
